// Page context is attached to every browser event so any insight can be broken
// down by page without per-event plumbing. The server renders it as JSON blocks
// (<AnalyticsContext>); the client merges them at init.

import type { HearingKind } from "../hearing-kind";

export type PageType =
  | "home"
  | "search"
  | "pricing"
  | "sponsor"
  | "signup"
  | "upgrade"
  | "login"
  | "preferences"
  | "settings"
  | "about"
  | "contact"
  | "legal"
  | "hearings_index"
  | "transcript"
  | "committee"
  | "content"
  | "other";

export interface PageContext {
  page_type?: PageType;
  is_authenticated?: boolean;
  /** Seat tier for signed-in users; null for anonymous readers. */
  plan?: string | null;
  /** Our own traffic (a signed-in super admin). Only ever set to true. */
  is_internal?: true;
  hearing_id?: string;
  hearing_kind?: HearingKind | "unclassified";
  chamber?: string;
  committee_code?: string | null;
  /** Hearing is inside its 7-day paid window. */
  is_fresh?: boolean;
  /** This viewer sees the paywall instead of the full transcript. */
  is_gated?: boolean;
  article_slug?: string;
}

const FIXED_PAGES: Record<string, PageType> = {
  "": "home",
  pricing: "pricing",
  sponsor: "sponsor",
  signup: "signup",
  upgrade: "upgrade",
  login: "login",
  preferences: "preferences",
  settings: "settings",
  about: "about",
  contact: "contact",
  privacy: "legal",
  terms: "legal",
  hearings: "hearings_index",
  search: "search",
  committees: "committee",
};

// /hearings/:congress/:chamber/:committee/:date/:title
const TRANSCRIPT_SEGMENTS = 6;

/**
 * Best-effort page type from the URL alone. Pages whose type can't be read
 * from the path (content articles live at the root) set it explicitly.
 */
export function pageTypeForPath(pathname: string): PageType {
  const segments = pathname.split("/").filter(Boolean);
  const first = segments[0] ?? "";
  if (first === "hearings" && segments.length >= TRANSCRIPT_SEGMENTS) return "transcript";
  return FIXED_PAGES[first] ?? "other";
}

/** JSON safe to inline in a <script type="application/json"> block. */
export function serializePageContext(context: PageContext): string {
  return JSON.stringify(context).replace(/</g, "\\u003c");
}

/** Merges context blocks in document order; later blocks win, malformed ones are skipped. */
export function parsePageContexts(blocks: readonly string[]): PageContext {
  const merged: PageContext = {};
  for (const block of blocks) {
    try {
      const value: unknown = JSON.parse(block);
      if (value && typeof value === "object" && !Array.isArray(value)) Object.assign(merged, value);
    } catch {
      // A broken block shouldn't cost us the rest of the context.
    }
  }
  return merged;
}
